import type { DatesRangeValue } from '@mantine/dates';
import type { MRT_PaginationState } from 'mantine-react-table';
import moment from 'moment';
import type {
  OrderHistoryDateField,
  OrderHistoryReportParams,
  OrderHistoryStatus
} from 'washday-sdk';

const baseDateFields: OrderHistoryDateField[] = [
  'created',
  'processed',
  'ready',
  'collected',
  'cancelled'
];

const homeServiceDateFields: OrderHistoryDateField[] = ['pickupScheduled', 'deliveryScheduled'];

export const orderHistoryStatuses: OrderHistoryStatus[] = [
  'created',
  'requested',
  'pickup',
  'pickingup',
  'pending_store_reception_validation',
  'cleaning',
  'ready',
  'delivery',
  'delivering',
  'collected',
  'cancelled',
  'uncollected'
];

export interface OrderHistoryFilters {
  dateField: OrderHistoryDateField;
  dateRange: DatesRangeValue;
  statuses: OrderHistoryStatus[];
  sequence: string;
  homeServiceOnly: boolean;
}

export interface OrderHistoryReportState {
  filters: OrderHistoryFilters;
  pagination: MRT_PaginationState;
}

export const getOrderHistoryDateFieldOptions = (
  homeServiceOnly: boolean
): OrderHistoryDateField[] => [
  ...baseDateFields,
  ...(homeServiceOnly ? homeServiceDateFields : [])
];

export const createDefaultOrderHistoryReportState = (
  referenceDate: Date = new Date()
): OrderHistoryReportState => ({
  filters: {
    dateField: 'created',
    dateRange: [moment(referenceDate).startOf('month').toDate(), moment(referenceDate).toDate()],
    statuses: [],
    sequence: '',
    homeServiceOnly: false
  },
  pagination: {
    pageIndex: 0,
    pageSize: 25
  }
});

export const applyOrderHistoryFilterChange = (
  state: OrderHistoryReportState,
  change: Partial<OrderHistoryFilters>
): OrderHistoryReportState => {
  const filters = { ...state.filters, ...change };
  if (!filters.homeServiceOnly && homeServiceDateFields.includes(filters.dateField)) {
    filters.dateField = 'created';
  }

  return {
    filters,
    pagination: {
      ...state.pagination,
      pageIndex: 0
    }
  };
};

export const resetOrderHistoryReportState = (
  referenceDate: Date = new Date()
): OrderHistoryReportState => createDefaultOrderHistoryReportState(referenceDate);

export const resetOrderHistoryPagination = (
  state: OrderHistoryReportState
): OrderHistoryReportState => ({
  ...state,
  pagination: {
    ...state.pagination,
    pageIndex: 0
  }
});

export const buildOrderHistoryReportQuery = (
  state: OrderHistoryReportState,
  storeId: string
): OrderHistoryReportParams => {
  const [from, to] = state.filters.dateRange;
  const normalizedFrom = from || to;
  const normalizedTo = to || from;

  return {
    storeId,
    dateField: state.filters.dateField,
    dateFrom: moment(normalizedFrom).format('YYYY-MM-DD'),
    dateTo: moment(normalizedTo).format('YYYY-MM-DD'),
    statuses: state.filters.statuses.length > 0 ? state.filters.statuses : undefined,
    sequence: state.filters.sequence.trim() || undefined,
    homeServiceOnly: state.filters.homeServiceOnly,
    page: state.pagination.pageIndex + 1,
    limit: state.pagination.pageSize,
    sortDirection: 'desc'
  };
};
